import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaEdit, FaSave } from 'react-icons/fa';

const NavbarManagement = () => {
  const [navbarTitle, setNavbarTitle] = useState('VidDownloader Pro');
  const [isEditing, setIsEditing] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const [saved, setSaved] = useState(false);

  // Load navbar title from localStorage
  useEffect(() => {
    const storedTitle = localStorage.getItem('navbar_title');
    if (storedTitle) {
      setNavbarTitle(storedTitle);
    }
  }, []);

  const handleEdit = () => {
    setTempTitle(navbarTitle);
    setIsEditing(true);
  };

  const handleSave = () => {
    setNavbarTitle(tempTitle);
    localStorage.setItem('navbar_title', tempTitle);
    setIsEditing(false);
    
    // Show saved indicator
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleCancel = () => {
    setTempTitle('');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Navbar Management</h1>
          <p className="text-gray-400">Customize your website's navigation bar</p>
        </div>
        
        {saved && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500 rounded-lg text-green-400"
          >
            <FaCheckCircle />
            <span className="font-medium">Saved!</span>
          </motion.div>
        )}
      </div>

      {/* Navbar Title Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0a0a1f] border border-gray-800 rounded-2xl p-6"
      >
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white mb-2">Website Title</h2>
          <p className="text-gray-400 text-sm">
            Change the title that appears in the navigation bar
          </p>
        </div>

        {!isEditing ? (
          <div className="flex items-center justify-between p-6 bg-[#1a1a3e] border border-gray-800 rounded-xl">
            <div>
              <p className="text-gray-400 text-sm mb-1">Current Title</p>
              <p className="text-white text-2xl font-bold">{navbarTitle}</p>
            </div>
            <motion.button
              onClick={handleEdit}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaEdit />
              Edit
            </motion.button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2 font-medium">New Title</label>
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                placeholder="Enter new navbar title"
                className="w-full px-4 py-3 bg-[#1a1a3e] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-colors"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <motion.button
                onClick={handleSave}
                disabled={!tempTitle.trim()}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaSave />
                Save Changes
              </motion.button>
              <motion.button
                onClick={handleCancel}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Preview Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#0a0a1f] border border-gray-800 rounded-2xl p-6"
      >
        <h2 className="text-xl font-bold text-white mb-4">Preview</h2>
        <div className="bg-[#1a1a3e] border border-gray-700 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <span className="text-white text-xl">⚡</span>
            </div>
            <span className="text-white font-bold text-xl">{navbarTitle}</span>
          </div>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          This is how your navbar title will appear on the website
        </p>
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4"
      >
        <p className="text-blue-400 text-sm">
          <strong>Note:</strong> Changes are saved to your browser's local storage and will take effect immediately on the website.
        </p>
      </motion.div>
    </div>
  );
};

export default NavbarManagement;
